import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../services/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { checkAndFixUserApproval } from '../utils/fixUserApproval';

const SimpleAuthContext = createContext();

export const useSimpleAuth = () => {
  const context = useContext(SimpleAuthContext);
  if (!context) {
    throw new Error('useSimpleAuth must be used within a SimpleAuthProvider');
  }
  return context;
};

export const SimpleAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userClaims, setUserClaims] = useState(null);

  useEffect(() => {
    console.log('🔧 SimpleAuth: Iniciando...');
    
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('🔧 SimpleAuth: Estado cambiado:', firebaseUser ? firebaseUser.uid : 'null');
      
      if (firebaseUser) {
        try {
          // Forzar refresco del token para obtener claims actualizados
          await firebaseUser.getIdToken(true);
          const idTokenResult = await firebaseUser.getIdTokenResult();
          const claims = idTokenResult.claims;
          
          console.log('🔧 SimpleAuth: Claims obtenidos:', claims);
          
          // Verificación adicional de seguridad y corrección automática
          const wasFixed = await checkAndFixUserApproval(claims);
          if (wasFixed) {
            console.log('✅ SimpleAuth: Usuario corregido automáticamente');
            // Forzar logout para que el usuario se vuelva a autenticar con los claims corregidos
            await auth.signOut();
            return;
          }
          
          // Si el usuario sigue teniendo el problema después de la corrección, forzar logout
          if (claims.approved === true && !claims.tenantId) {
            console.warn('⚠️ SimpleAuth: Usuario aprobado sin tenantId - forzando logout');
            await auth.signOut();
            return;
          }
          
          setUser(firebaseUser);
          setUserClaims(claims);
        } catch (error) {
          console.error('🔧 SimpleAuth: Error obteniendo claims:', error);
          setUser(firebaseUser);
          setUserClaims(null);
        }
      } else {
        setUser(null);
        setUserClaims(null);
      }
      
      setLoading(false);
    });

    return () => {
      console.log('🔧 SimpleAuth: Limpiando...');
      unsubscribe();
    };
  }, []);

  const value = {
    user,
    userClaims,
    loading,
    isAuthenticated: !!user,
    isAdmin: userClaims?.isAdmin === true || userClaims?.role === 'businessAdmin' || userClaims?.role === 'globalAdmin',
    isGlobalAdmin: userClaims?.globalAdmin === true || userClaims?.role === 'globalAdmin',
    isBusinessAdmin: userClaims?.isAdmin === true || userClaims?.role === 'businessAdmin',
    isClient: userClaims?.role === 'client',
    tenantId: userClaims?.tenantId
  };

  return (
    <SimpleAuthContext.Provider value={value}>
      {children}
    </SimpleAuthContext.Provider>
  );
};
